import React, { useState, useEffect } from 'react';

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  placeholder?: string;
  helperText?: string;
  multiline?: boolean;
}

const TextField = ({ label, value, onChange, placeholder, helperText, multiline }: TextFieldProps) => {
  return (
    <div className="space-y-1">
      <label className="block text-sm text-gray-400">{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[100px]"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      )}
      {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
    </div>
  );
};

interface ProductsMiniAppTranslationProps {
  language: {
    label: string;
  };
  formData: {
    title?: string;
    description?: string;
    labels?: {
      matchingProduct?: string;
      matchingProducts?: string;
      close?: string;
      viewProduct?: string;
      addToCart?: string;
      stockLabel?: string;
      price?: string;
    };
    productDetails?: Record<string, any>;
  };
  onChange: (data: any) => void;
}

export function ProductsMiniAppTranslation({ language, formData, onChange }: ProductsMiniAppTranslationProps) {
  const [localFormData, setLocalFormData] = useState(formData);

  useEffect(() => {
    setLocalFormData(formData);
  }, [formData]);

  const handleChange = (field: string, value: string) => {
    const newFormData = { ...localFormData };
    
    if (field.startsWith('labels.')) {
      const labelField = field.split('.')[1];
      newFormData.labels = { ...newFormData.labels, [labelField]: value };
    } else {
      (newFormData as any)[field] = value;
    }
    
    setLocalFormData(newFormData);
    setTimeout(() => {
      onChange(newFormData);
    }, 10);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4">
        <h3 className="text-lg font-semibold text-white mb-2">App information</h3>
        <TextField
          label={`App title in ${language.label}`}
          value={localFormData.title || ''}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="Products"
        />
        
        <TextField
          label={`App description in ${language.label}`}
          value={localFormData.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Explore our products"
          multiline
        />
      </div>

      <div className="bg-white/5 p-4 rounded-lg space-y-4">
        <h3 className="text-lg font-semibold text-white">Labels and messages</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField
            label="Matching product"
            value={localFormData.labels?.matchingProduct || ''}
            onChange={(e) => handleChange('labels.matchingProduct', e.target.value)}
            placeholder="Matching product"
            helperText="Text displayed when one product matches"
          />
          
          <TextField
            label="Matching products"
            value={localFormData.labels?.matchingProducts || ''}
            onChange={(e) => handleChange('labels.matchingProducts', e.target.value)}
            placeholder="Matching products"
            helperText="Text displayed when multiple products match"
          />
          
          <TextField
            label="Close button"
            value={localFormData.labels?.close || ''}
            onChange={(e) => handleChange('labels.close', e.target.value)}
            placeholder="Close"
            helperText="Close button in product modal"
          />
          
          <TextField
            label="View product button"
            value={localFormData.labels?.viewProduct || ''}
            onChange={(e) => handleChange('labels.viewProduct', e.target.value)}
            placeholder="View product"
            helperText="Button to view product details"
          />
          
          <TextField
            label="Add to cart button"
            value={localFormData.labels?.addToCart || ''}
            onChange={(e) => handleChange('labels.addToCart', e.target.value)}
            placeholder="Add to cart"
            helperText="Button to add product to cart"
          />
          
          <TextField
            label="Stock label"
            value={localFormData.labels?.stockLabel || ''}
            onChange={(e) => handleChange('labels.stockLabel', e.target.value)}
            placeholder="Stock"
            helperText="Label for stock information"
          />
          
          <TextField
            label="Price label"
            value={localFormData.labels?.price || ''}
            onChange={(e) => handleChange('labels.price', e.target.value)}
            placeholder="Price"
            helperText="Label for price information"
          />
        </div>
      </div>
    </div>
  );
} 